import React, { useEffect, useRef, useState } from "react";
import { Upload, Eye, Trash2, Loader2, Mail, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export const STATUS_STYLE = {
  missing: { label: "Manquant", cls: "bg-norea-bg text-norea-textMuted border-norea-border" },
  valid: { label: "Valide", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  expiring: { label: "Expire bientôt", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  expired: { label: "Expiré", cls: "bg-red-50 text-red-700 border-red-300" },
};

export function StatusBadge({ status, testid }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.missing;
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap ${s.cls}`} data-testid={testid}>{s.label}</span>;
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "");

async function openDoc(subId, key) {
  try {
    const { data } = await api.get(`/subcontractors/${subId}/documents/${key}/file`, { responseType: "blob" });
    window.open(URL.createObjectURL(data), "_blank", "noopener");
  } catch {
    toast.error("Ouverture impossible");
  }
}

export function DocRow({ sub, type, onUpdated }) {
  const doc = (sub.documents || {})[type.key];
  const status = sub.doc_statuses?.[type.key] || "missing";
  const [busy, setBusy] = useState(false);
  const [expiry, setExpiry] = useState(doc?.expires_at || "");
  const fileRef = useRef(null);
  useEffect(() => setExpiry(doc?.expires_at || ""), [doc?.expires_at]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const { data } = await fn();
      onUpdated(data);
      if (ok) toast.success(ok);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const upload = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (expiry) fd.append("expires_at", expiry);
    run(() => api.post(`/subcontractors/${sub.id}/documents/${type.key}`, fd), `${type.label} enregistré`);
  };
  const saveExpiry = (v) => {
    setExpiry(v);
    if (doc) run(() => api.patch(`/subcontractors/${sub.id}/documents/${type.key}`, { expires_at: v || null }), "Date d'expiration mise à jour");
  };
  const remove = () => {
    if (window.confirm(`Supprimer le document « ${type.label} » ?`)) run(() => api.delete(`/subcontractors/${sub.id}/documents/${type.key}`), "Document supprimé");
  };

  const tid = `doc-${type.key}`;
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2 py-3 border-b border-norea-border last:border-0" data-testid={tid}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-norea-text">{type.label}</span>
          <StatusBadge status={status} testid={`${tid}-status`} />
          {doc?.source === "email" && (
            <span className="inline-flex items-center gap-1 text-[10px] text-norea-textMuted" title="Reçu par email et classé automatiquement"><Mail className="w-3 h-3" /> par email</span>
          )}
        </div>
        <div className="text-xs text-norea-textMuted mt-0.5 truncate" data-testid={`${tid}-filename`}>
          {doc ? `${doc.filename} · déposé le ${fmtDate(doc.uploaded_at)}` : "Aucun fichier"}
        </div>
        {type.expires && (
          <label className="mt-1.5 inline-flex items-center gap-2 text-xs text-norea-textMuted">
            Expire le
            <input type="date" value={expiry || ""} onChange={(e) => saveExpiry(e.target.value)} className="px-1.5 py-0.5 border border-norea-border rounded text-xs bg-white" data-testid={`${tid}-expiry`} />
          </label>
        )}
      </div>
      <div className="flex items-start gap-1">
        <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${tid}-file-input`} />
        <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-secondary text-xs px-2 py-1" data-testid={`${tid}-upload-btn`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {doc ? "Remplacer" : "Déposer"}
        </button>
        {doc && (
          <>
            <button onClick={() => openDoc(sub.id, type.key)} className="p-1.5 rounded text-norea-textMuted hover:text-norea-primary hover:bg-norea-surfaceHover" title="Visualiser" data-testid={`${tid}-view-btn`}><Eye className="w-4 h-4" /></button>
            <button onClick={remove} className="p-1.5 rounded text-norea-textMuted hover:text-red-600 hover:bg-red-50" title="Supprimer" data-testid={`${tid}-delete-btn`}><Trash2 className="w-4 h-4" /></button>
          </>
        )}
      </div>
    </div>
  );
}

export function DocsSummary({ sub }) {
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`sub-docs-summary-${sub.id}`}>
      <FileCheck2 className="w-3.5 h-3.5 text-norea-textMuted" />
      <span className="tabular-nums text-sm">{sub.docs_present}/{sub.docs_total}</span>
      {sub.docs_expired > 0 && <StatusBadge status="expired" />}
      {!sub.docs_expired && sub.docs_expiring > 0 && <StatusBadge status="expiring" />}
    </span>
  );
}
